import React, {useEffect, useState} from "react";
import {Modal, Form, Input, DatePicker, Button, message, Row, Col, Switch, Select, Checkbox, Popconfirm} from "antd";
import {DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import moment from "moment";
import dayjs from "dayjs";
import Api from "../api";

const {RangePicker} = DatePicker;
const {TextArea} = Input;

const CreateCompetition = ({callback, competitionData, setCompetitionData}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [form] = Form.useForm();
    const [isPremium, setIsPremium] = useState(false);
    const [isBingo, setIsBingo] = useState(false);
    const [bingos, setBingos] = useState([]);
    const [billingPlans, setBillingPlans] = useState([]);
    const [showBillingPlansConfirmed, setShowBillingPlansConfirmed] = useState(false);
    const [billingPlanConfigs, setBillingPlanConfigs] = useState([]);

    useEffect(() => {
        if (competitionData) {
            setIsModalOpen(true);
            setIsPremium(!!competitionData.is_paid); // Set premium checkbox state
            setIsBingo(competitionData.type === 'bingo'); // Set bingo select

            if (competitionData.billing_plan_ids && competitionData.billing_plan_ids.length > 0) {
                setShowBillingPlansConfirmed(true);
                setBillingPlanConfigs(competitionData.billing_plan_ids.map(config => ({
                    billing_plan_id: config.billing_plan_id,
                    access_since: config.access_since ? dayjs(config.access_since) : null,
                    access_until: config.access_until ? dayjs(config.access_until) : null
                })));
            }

            form.setFieldsValue({
                name: competitionData.name,
                image: competitionData.image,
                dates: [dayjs(competitionData.begin_date), dayjs(competitionData.end_date)], // Set start and end date
                enabled: competitionData.enabled,
                is_paid: competitionData.is_paid,
                type: competitionData.type,
                product_id: competitionData.product_id || "",
                bingo_id: competitionData.bingo_id || "",
                summary: competitionData.summary
            });
        } else {
            form.setFieldsValue({
                name: '',
                image: '',
                dates: [], // Set start and end date
                enabled: 0, // Convert enabled to boolean
                is_paid: '',
                type: '',
                product_id: "",
                bingo_id: "",
                summary: ""
            });
        }
    }, [competitionData, form]);

    useEffect(() => {
        Api.get('/admin/v2/bingos').then(res => {
            setBingos(res.data);
        })

        Api.get('/admin/banners/available-billing').then(res => {
            setBillingPlans(res.data);
        })
    }, [])

    const handleCancel = () => {
        setCompetitionData(null); // Reset competitionData (null to close the modal)
        setIsModalOpen(false);
        setIsPremium(false);
        setIsBingo(false);
        setShowBillingPlansConfirmed(false);
        setBillingPlanConfigs([]);
        form.resetFields(); // Reset form fields when modal closes
    };

    const handleShowBillingPlans = () => {
        Modal.confirm({
            title: 'Enable Billing Plan Access',
            content: 'You are about to grant free access to selected billing plans for this competition. Users with these plans will be able to access this competition. Do you want to continue?',
            okText: 'Yes, Enable Access',
            cancelText: 'Cancel',
            onOk: () => {
                setShowBillingPlansConfirmed(true);
            }
        });
    };

    const handleAddBillingPlan = () => {
        setBillingPlanConfigs([...billingPlanConfigs, {
            billing_plan_id: null,
            access_since: null,
            access_until: null
        }]);
    };

    const handleRemoveBillingPlan = (index) => {
        const newConfigs = billingPlanConfigs.filter((_, i) => i !== index);
        setBillingPlanConfigs(newConfigs);
    };

    const handleBillingPlanChange = (index, field, value) => {
        const newConfigs = [...billingPlanConfigs];
        newConfigs[index][field] = value;
        setBillingPlanConfigs(newConfigs);
    };

    const getAvailableBillingPlans = (currentPlanId) => {
        const selectedPlanIds = billingPlanConfigs
            .map(config => config.billing_plan_id)
            .filter(id => id && id !== currentPlanId);

        return billingPlans.filter(plan => !selectedPlanIds.includes(plan.plan_id));
    };

    const validateBillingPlanDates = () => {
        for (let i = 0; i < billingPlanConfigs.length; i++) {
            const config = billingPlanConfigs[i];

            if (!config.billing_plan_id) {
                message.error('Please select a billing plan for all rows');
                return false;
            }

            if (config.access_since && config.access_until) {
                if (config.access_until.isBefore(config.access_since)) {
                    message.error('Access Until date must be after or equal to Access Since date');
                    return false;
                }
            }
        }
        return true;
    };

    const handleSubmit = (values) => {
        const {name, image, dates, enabled, summary, type, is_paid, product_id, bingo_id} = values;

        // Validate billing plan configurations
        if (billingPlanConfigs.length > 0 && !validateBillingPlanDates()) {
            return;
        }

        let competitionPayload = {
            name,
            type,
            enabled: enabled,
            summary,
            is_paid: isPremium ? 1 : 0, // Ensure correct value
        };

        if (image) {
            competitionPayload.image = image;
        }

        if (dates) {
            const [startDate, endDate] = dates;

            competitionPayload.begin_date = startDate.format("YYYY-MM-DD");
            competitionPayload.end_date = endDate.format("YYYY-MM-DD");
        }

        if (isPremium) {
            competitionPayload.product_id = product_id;
        }

        if (isBingo) {
            competitionPayload.bingo_id = bingo_id;
        }

        if (type === 'steps') {
            competitionPayload.include_leaderboard = true;
        }

        // Convert billing plan configurations to the new format
        if (billingPlanConfigs.length > 0) {
            competitionPayload.billing_plan_ids = billingPlanConfigs.map(config => ({
                billing_plan_id: config.billing_plan_id,
                access_since: config.access_since ? config.access_since.format("YYYY-MM-DD") : null,
                access_until: config.access_until ? config.access_until.format("YYYY-MM-DD") : null
            }));
        }

        // If competitionData is provided, update the competition
        const request = competitionData
            ? Api.put(`/admin/competitions/${competitionData.id}`, competitionPayload) // Update
            : Api.post("/admin/competitions", competitionPayload); // Create

        request
            .then((response) => {
                callback();
                message.success(competitionData ? "Competition updated successfully!" : "Competition created successfully!");
                form.resetFields();
                setIsModalOpen(false);
            })
            .catch((error) => {
                message.error("Failed to process competition. Please try again.");
            });
    };

    return (
        <div>
            <Button type="primary" onClick={() => setIsModalOpen(true)}>
                Create Competition
            </Button>

            <Modal
                destroyOnClose={true}
                title={<div
                    style={{textAlign: "center"}}>{competitionData ? "Edit Competition" : "Create New Competition"}</div>}
                open={isModalOpen}
                onCancel={handleCancel}
                footer={null} // Footer is handled by form buttons
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        name: competitionData ? competitionData.name : "",
                        image: competitionData ? competitionData.image : "",
                        dates: competitionData ? [dayjs(competitionData.begin_date), dayjs(competitionData.end_date)] : [],
                        enabled: competitionData ? competitionData.enabled : null, // Set enabled based on competitionData
                    }}
                >
                    <Form.Item
                        name="name"
                        label="Competition Name"
                        rules={[{required: true, message: "Please enter the competition name"}]}
                    >
                        <Input placeholder="Enter competition name"/>
                    </Form.Item>

                    <Form.Item
                        name="image"
                        label="Image url"
                    >
                        <Input placeholder="Image url"/>
                    </Form.Item>

                    <Form.Item
                        name="summary"
                        label="Summary"
                    >
                        <TextArea placeholder="Description"/>
                    </Form.Item>

                    <Form.Item
                        name="dates"
                        label="Start and End Date"
                    >
                        <RangePicker/>
                    </Form.Item>

                    <Form.Item required={true} initialValue={"steps"} name="type" label="Type">
                        <Select onChange={(e) => setIsBingo(e === 'bingo')}>
                            <Select.Option value={"steps"}>Steps</Select.Option>
                            <Select.Option value={"bingo"}>Bingo</Select.Option>
                        </Select>
                    </Form.Item>

                    {isBingo && (
                        <Form.Item required={true} name="bingo_id" label="Bingo">
                            <Select onChange={(e) => setIsBingo(e)}>
                                {bingos.map(bingo => {
                                    return (
                                        <Select.Option value={bingo.id}>{bingo.name}</Select.Option>
                                    )
                                })}
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item label="" name="is_paid" valuePropName="checked">
                        <Checkbox checked={isPremium} onChange={(e) => setIsPremium(e.target.checked)}>
                            Premium access only
                        </Checkbox>
                    </Form.Item>

                    {isPremium && (
                        <>
                            <Form.Item
                                name="product_id"
                                label={<div><a target="_blank" href="https://fitlap.ee/amember/admin-products">aMember</a> Product ID</div>}
                                rules={[{required: true, message: "Please enter the product ID for the premium challenge"}]}
                            >
                                <Input placeholder="Enter Product ID"/>
                            </Form.Item>


                            {!showBillingPlansConfirmed ? (
                                <Form.Item label="Grant Access to Billing Plans">
                                    <Button type="dashed" onClick={handleShowBillingPlans}>
                                        + Grant access to specific billing plans
                                    </Button>
                                </Form.Item>
                            ) : (
                                <div style={{ marginBottom: 24 }}>
                                    <div style={{ marginBottom: 12, fontWeight: 500 }}>
                                        Billing Plan Access Configuration
                                    </div>
                                    <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
                                        Configure which billing plans have access to this competition and their promotional periods
                                    </div>

                                    {billingPlanConfigs.length > 0 && (
                                        <div style={{ marginBottom: 12 }}>
                                            {billingPlanConfigs.map((config, index) => (
                                                <div key={index} style={{
                                                    marginBottom: 16,
                                                    padding: 16,
                                                    border: '1px solid #f0f0f0',
                                                    borderRadius: 4,
                                                    backgroundColor: '#fafafa'
                                                }}>
                                                    <Row gutter={[12, 12]} align="middle">
                                                        <Col span={24}>
                                                            <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Billing Plan *</div>
                                                            <Select
                                                                style={{ width: '100%' }}
                                                                placeholder="Select billing plan"
                                                                value={config.billing_plan_id}
                                                                onChange={(value) => handleBillingPlanChange(index, 'billing_plan_id', value)}
                                                            >
                                                                {getAvailableBillingPlans(config.billing_plan_id).map(plan => (
                                                                    <Select.Option key={plan.plan_id} value={plan.plan_id}>
                                                                        {plan.title}
                                                                    </Select.Option>
                                                                ))}
                                                            </Select>
                                                        </Col>
                                                        <Col span={11}>
                                                            <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Access Since (Promotion Start)</div>
                                                            <DatePicker
                                                                style={{ width: '100%' }}
                                                                placeholder="Start date (optional)"
                                                                value={config.access_since}
                                                                onChange={(date) => handleBillingPlanChange(index, 'access_since', date)}
                                                                format="YYYY-MM-DD"
                                                            />
                                                        </Col>
                                                        <Col span={11}>
                                                            <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Access Until (Promotion End)</div>
                                                            <DatePicker
                                                                style={{ width: '100%' }}
                                                                placeholder="End date (optional)"
                                                                value={config.access_until}
                                                                onChange={(date) => handleBillingPlanChange(index, 'access_until', date)}
                                                                format="YYYY-MM-DD"
                                                                disabledDate={(current) => {
                                                                    if (config.access_since) {
                                                                        return current && current.isBefore(config.access_since, 'day');
                                                                    }
                                                                    return false;
                                                                }}
                                                            />
                                                        </Col>
                                                        <Col span={2}>
                                                            <div style={{ marginBottom: 4, fontSize: 12, color: 'transparent' }}>-</div>
                                                            <Popconfirm
                                                                title="Remove this billing plan?"
                                                                onConfirm={() => handleRemoveBillingPlan(index)}
                                                                okText="Yes"
                                                                cancelText="No"
                                                            >
                                                                <Button
                                                                    type="text"
                                                                    danger
                                                                    icon={<DeleteOutlined />}
                                                                    block
                                                                />
                                                            </Popconfirm>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <Button
                                        type="dashed"
                                        onClick={handleAddBillingPlan}
                                        icon={<PlusOutlined />}
                                        disabled={billingPlanConfigs.length >= billingPlans.length}
                                        block
                                    >
                                        Add Billing Plan
                                    </Button>

                                    {billingPlanConfigs.length >= billingPlans.length && (
                                        <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                                            All available billing plans have been added
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    <Form.Item
                        name="enabled"
                        valuePropName="checked"
                        initialValue={true} // Default value is enabled (1)
                    >
                        <Switch checkedChildren="Active" unCheckedChildren="Inactive"/>
                    </Form.Item>

                    <Form.Item>
                        <Row type="flex" justify="center">
                            <Button type="primary" htmlType="submit" style={{marginRight: "10px"}}>
                                {competitionData ? "Update" : "Submit"}
                            </Button>
                            <Button onClick={handleCancel}>Cancel</Button>
                        </Row>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default CreateCompetition;